import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../firebase/config';
import Toast from './Toast';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error' | 'info';
    isVisible: boolean;
  }>({
    message: '',
    type: 'info',
    isVisible: false
  });
  const navigate = useNavigate();

  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    setToast({
      message,
      type,
      isVisible: true
    });
  };

  const hideToast = () => {
    setToast(prev => ({ ...prev, isVisible: false }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await sendPasswordResetEmail(auth, email);
      showToast('Password reset email sent! Please check your inbox.', 'success');
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error: any) {
      let errorMessage = 'Failed to send password reset email. Please try again.';
      
      if (error.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this email address.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address.';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Too many requests. Please try again later.';
      }
      
      showToast(errorMessage, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex">
      {/* Background Image with Blur */}
      <div 
        className="fixed inset-0 w-full lg:w-[70%] h-full bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: 'url(/assets/background3.png)',
          filter: 'blur(0px)',
          opacity: '0.8',
          transform: 'scale(1.00)'
        }}
      />
      
      {/* Overlay to darken the background */}
      <div className="fixed inset-0 bg-gray-900 bg-opacity-20" />
      
      {/* Left Side - Background visible (70%) */}
      <div className="relative z-10 w-0 lg:w-[70%] flex items-center justify-center p-8">
        <div className="hidden lg:block">
          {/* You can add a logo or tagline here if needed */}
        </div>
      </div>

      {/* Right Side - Forgot Password Form (30%) */}
      <div className="relative z-10 w-full lg:w-[30%] min-h-screen bg-white/40 backdrop-blur-xl shadow-2xl flex items-center justify-center p-6 lg:p-8">
        <div className="w-full max-w-md space-y-4">
          <div>
            <div className="mx-auto h-20 w-20 flex items-center justify-center">
              <img src="/assets/ledger.png" alt="Accounts System" className="h-full w-full object-contain drop-shadow-lg" />
            </div>
            <h2 className="mt-3 text-center text-lg font-semibold text-gray-900 drop-shadow-md">
              RESET YOUR PASSWORD
            </h2>
            <p className="mt-1 text-center text-sm font-medium text-gray-800 drop-shadow-sm">
              Enter your email to receive a password reset link
            </p>
          </div>
          
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-1 drop-shadow-sm">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="appearance-none block w-full px-3 py-2 bg-white/60 backdrop-blur-sm border-2 border-white/40 rounded-lg shadow-sm placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-400 sm:text-sm"
                placeholder="Enter your email"
                disabled={isLoading}
              />
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="group relative w-full flex justify-center py-2 px-4 border-2 border-orange-500/50 text-sm font-semibold rounded-lg text-white bg-orange-500/90 backdrop-blur-sm hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg"
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending...
                  </div>
                ) : (
                  <>
                    <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                      <svg className="h-5 w-5 text-white/80 group-hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </span>
                    Send Reset Link
                  </>
                )}
              </button>
            </div>

            <div className="text-center">
              <p className="text-sm font-medium text-gray-900 drop-shadow-sm">
                Remember your password?{' '}
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="font-semibold text-orange-600 hover:text-orange-700 transition-colors"
                >
                  Back to Login
                </button>
              </p>
            </div>
          </form>
          
          {/* Logos Section */}
          <div className="mt-2 space-y-1">
            <div className="p-4 flex items-center justify-center">
              <img src="/assets/barq_logo-transparent.png" alt="Barq Cargo System" className="h-14 object-contain" />
            </div>
            <div className="p-4 flex items-center justify-center">
              <img src="/assets/vector_logo-transparent.png" alt="Vector Logistics" className="h-14 object-contain" />
            </div>
          </div>
          
          <Toast
            message={toast.message}
            type={toast.type}
            isVisible={toast.isVisible}
            onClose={hideToast}
          />
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;

